/** Shared types and formatters for outbound call UI. */

import {
  datetimeLocalInTimezoneToUtc,
  type OutboundCallDraft
} from '@oysterjs/ais/api/data/calls/input';
export {
  buildOutboundCallMutationInput,
  datetimeLocalInTimezoneToUtc
} from '@oysterjs/ais/api/data/calls/input';
export type { OutboundCallDraft } from '@oysterjs/ais/api/data/calls/input';

import {
  DraftOutboundCallToolInput,
  OutboundCallFieldsFragment,
  OutboundCallStatus
} from '../../api/graphql';
import { browserTimezone } from '../../lib/agency-timezones';

/** Supported outbound schedule timezones — matches Settings → Voice transfer hours. */
export const OUTBOUND_CALL_TIMEZONES = [
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/New_York', label: 'Eastern (ET)' }
] as const;

export type OutboundCallTimezone = (typeof OUTBOUND_CALL_TIMEZONES)[number]['value'];

const OUTBOUND_CALL_TIMEZONE_SET = new Set<string>(
  OUTBOUND_CALL_TIMEZONES.map((timezone) => timezone.value)
);

const OUTBOUND_CALL_TIMEZONE_ABBREVS: Record<OutboundCallTimezone, string> = {
  'America/Los_Angeles': 'PT',
  'America/Denver': 'MT',
  'America/Chicago': 'CT',
  'America/New_York': 'ET'
};

/** Common US IANA aliases mapped to the supported outbound buckets above. */
const OUTBOUND_CALL_TIMEZONE_ALIASES: Record<string, OutboundCallTimezone> = {
  'America/Vancouver': 'America/Los_Angeles',
  'America/Tijuana': 'America/Los_Angeles',
  'America/Phoenix': 'America/Denver',
  'America/Edmonton': 'America/Denver',
  'America/Boise': 'America/Denver',
  'America/Winnipeg': 'America/Chicago',
  'America/Mexico_City': 'America/Chicago',
  'America/Toronto': 'America/New_York',
  'America/Detroit': 'America/New_York',
  'America/Montreal': 'America/New_York',
  'America/Indiana/Indianapolis': 'America/New_York',
  'America/Kentucky/Louisville': 'America/New_York'
};

const getTimezoneOffsetMinutes = (timeZone: string, date = new Date()): number | null => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'longOffset'
    }).formatToParts(date);
    const raw = parts.find((part) => part.type === 'timeZoneName')?.value;
    if (!raw) {
      return null;
    }
    const match = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(raw);
    if (!match) {
      return null;
    }
    const sign = match[1] === '-' ? -1 : 1;
    const hours = Number(match[2]);
    const minutes = Number(match[3] ?? '0');
    return sign * (hours * 60 + minutes);
  } catch {
    return null;
  }
};

/** Map a browser or legacy IANA zone to the nearest supported outbound bucket. */
export const normalizeOutboundCallTimezone = (timezone: string): OutboundCallTimezone => {
  const trimmed = timezone.trim();
  if (OUTBOUND_CALL_TIMEZONE_SET.has(trimmed)) {
    return trimmed as OutboundCallTimezone;
  }
  const aliased = OUTBOUND_CALL_TIMEZONE_ALIASES[trimmed];
  if (aliased) {
    return aliased;
  }

  const sourceOffset = getTimezoneOffsetMinutes(trimmed);
  if (sourceOffset === null) {
    return 'America/New_York';
  }

  let best: OutboundCallTimezone = 'America/New_York';
  let bestDelta = Infinity;
  for (const option of OUTBOUND_CALL_TIMEZONES) {
    const offset = getTimezoneOffsetMinutes(option.value);
    if (offset === null) {
      continue;
    }
    const delta = Math.abs(offset - sourceOffset);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = option.value;
    }
  }
  return best;
};

/** Resolve an outbound schedule timezone from tool/API input or the browser default. */
export const resolveOutboundCallTimezone = (timezone?: string): OutboundCallTimezone => {
  const trimmed = timezone?.trim() ?? '';
  if (trimmed && OUTBOUND_CALL_TIMEZONE_SET.has(trimmed)) {
    return trimmed as OutboundCallTimezone;
  }
  if (trimmed && OUTBOUND_CALL_TIMEZONE_ALIASES[trimmed]) {
    return OUTBOUND_CALL_TIMEZONE_ALIASES[trimmed];
  }
  return normalizeOutboundCallTimezone(browserTimezone());
};

export const outboundCallTimezoneOptions = (timezone: string) => {
  const normalized = resolveOutboundCallTimezone(timezone);
  if (OUTBOUND_CALL_TIMEZONE_SET.has(normalized)) {
    return OUTBOUND_CALL_TIMEZONES;
  }
  return [{ value: normalized, label: normalized }, ...OUTBOUND_CALL_TIMEZONES];
};

export const formatOutboundCallTimezoneAbbrev = (timezone: string): string =>
  OUTBOUND_CALL_TIMEZONE_ABBREVS[resolveOutboundCallTimezone(timezone)] ?? timezone;

/** Default schedule timezone for new outbound calls (browser mapped to PT/MT/CT/ET). */
export const defaultScheduledTimezone = (): OutboundCallTimezone =>
  resolveOutboundCallTimezone(undefined);

/** Copy shown before the frequency query has data (limits come from the API once loaded). */
export const OUTBOUND_CALL_FREQUENCY_TEASER =
  'AI outbound calls to each number are limited over a rolling window.';

/** Copy for the rolling per-number AI outbound call frequency helper. */
export const formatOutboundCallFrequencyDescription = (
  count: number,
  limit: number,
  windowDays: number
): string =>
  `${count} of ${limit} AI outbound calls to this number in the last ${windowDays} days.`;

/** GraphQL input field paths for each draft key (used to clear validation errors on edit). */
export const OUTBOUND_CALL_DRAFT_VALIDATION_PATHS: Partial<
  Record<keyof OutboundCallDraft, string[]>
> = {
  voiceAgentId: ['voiceAgentId'],
  customerName: ['customer', 'name'],
  customerPhone: ['customer', 'phone'],
  scheduleLater: ['schedule', 'scheduleLater'],
  scheduledAt: ['schedule', 'scheduledAt'],
  scheduledTimezone: ['schedule', 'timezone'],
  callObjective: ['callConfig', 'callObjective'],
  customerContext: ['callConfig', 'customerContext'],
  guidelines: ['callConfig', 'guidelines'],
  callbackPhone: ['callConfig', 'callbackPhone']
};

export const emptyCallDraft = (voiceAgentId = ''): OutboundCallDraft => ({
  voiceAgentId,
  customerName: '',
  customerPhone: '',
  scheduleLater: true,
  scheduledAt: '',
  scheduledTimezone: defaultScheduledTimezone(),
  callObjective: '',
  customerContext: '',
  guidelines: '',
  callbackPhone: '',
  leaveVoicemail: true,
  fileIds: []
});

/** The fields a link into the create dialog may prefill. */
export type OutboundCallDraftPrefill = Partial<
  Pick<OutboundCallDraft, 'customerName' | 'customerPhone'>
>;

const OUTBOUND_CALL_PREFILL_PARAMS = ['customerName', 'customerPhone'] as const;

/** Prefill travels in the URL, so a filled form survives a reload and can be linked to. */
export const outboundCallPrefillSearch = (prefill: OutboundCallDraftPrefill): string => {
  const params = new URLSearchParams();
  for (const key of OUTBOUND_CALL_PREFILL_PARAMS) {
    const value = prefill[key]?.trim();
    if (value) {
      params.set(key, value);
    }
  }
  const search = params.toString();
  return search ? `?${search}` : '';
};

export const outboundCallPrefillFromSearch = (
  params: URLSearchParams
): OutboundCallDraftPrefill => {
  const prefill: OutboundCallDraftPrefill = {};
  for (const key of OUTBOUND_CALL_PREFILL_PARAMS) {
    const value = params.get(key);
    if (value) {
      prefill[key] = value;
    }
  }
  return prefill;
};

/**
 * Converts a Copilot tool input into an editable OutboundCallDraft, applying timezone
 * validation and defaults for any fields the LLM left blank.
 */
export const outboundCallDraftFromTool = (input: DraftOutboundCallToolInput): OutboundCallDraft => {
  const scheduledTimezone = resolveOutboundCallTimezone(input.timezone);

  // datetime-local inputs need yyyy-MM-ddTHH:mm. The model often sends full ISO strings.
  const scheduledAtRaw = input.scheduledAt?.trim() ?? '';
  let scheduledAt = '';
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(scheduledAtRaw)) {
    scheduledAt = scheduledAtRaw;
  } else if (scheduledAtRaw) {
    scheduledAt = scheduledAtToDatetimeLocal(scheduledAtRaw, scheduledTimezone);
  }

  return {
    voiceAgentId: '',
    customerName: input.customerName ?? '',
    customerPhone: input.customerPhone ?? '',
    callObjective: input.callObjective,
    customerContext: input.customerContext ?? '',
    guidelines: input.guidelines ?? '',
    callbackPhone: input.callbackPhone ?? '',
    leaveVoicemail: input.leaveVoicemail,
    scheduleLater: true,
    scheduledAt,
    scheduledTimezone,
    fileIds: []
  };
};

export const draftFromOutboundCall = (call: OutboundCallFieldsFragment): OutboundCallDraft => ({
  voiceAgentId: call.voiceAgent.id,
  customerName: call.customer.name ?? '',
  customerPhone: call.customer.phone,
  scheduleLater: !!call.schedule.scheduledAt,
  scheduledAt: scheduledAtToDatetimeLocal(call.schedule.scheduledAt, call.schedule.timezone),
  scheduledTimezone: resolveOutboundCallTimezone(call.schedule.timezone),
  callObjective: call.callConfig.callObjective,
  customerContext: call.callConfig.customerContext ?? '',
  guidelines: call.callConfig.guidelines ?? '',
  callbackPhone: call.callConfig.callbackPhone ?? '',
  leaveVoicemail: call.callConfig.leaveVoicemail,
  fileIds: call.callConfig.fileIds
});

export const parseScheduledAtValue = (
  scheduledAt: Date | string | null | undefined
): Date | null => {
  if (scheduledAt == null || scheduledAt === '') {
    return null;
  }
  const date = scheduledAt instanceof Date ? scheduledAt : new Date(scheduledAt);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
};

export const scheduledAtToDatetimeLocal = (
  scheduledAt: Date | string | null | undefined,
  timezone: string
): string => {
  const date = parseScheduledAtValue(scheduledAt);
  if (!date || !timezone.trim()) {
    return '';
  }
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
};

/** Keep the same UTC instant when the schedule timezone picker changes. */
export const convertScheduledAtBetweenTimezones = (
  datetimeLocal: string,
  fromTimezone: string,
  toTimezone: string
): string => {
  if (!datetimeLocal.trim() || fromTimezone === toTimezone) {
    return datetimeLocal;
  }
  const utc = datetimeLocalInTimezoneToUtc(datetimeLocal, fromTimezone);
  if (!utc) {
    return datetimeLocal;
  }
  return scheduledAtToDatetimeLocal(utc, toTimezone);
};

export const formatOutboundCallScheduleFromApi = (
  call: Pick<OutboundCallFieldsFragment, 'schedule' | 'createdAt'>
): string => {
  // Call-now rows stamp scheduledAt on create/dial; fall back to createdAt for
  // older rows that still have a null schedule.
  const date =
    parseScheduledAtValue(call.schedule.scheduledAt) ?? parseScheduledAtValue(call.createdAt);
  if (!date) {
    return '—';
  }
  const formatted = new Intl.DateTimeFormat('en-US', {
    timeZone: call.schedule.timezone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
  return `${formatted} ${formatOutboundCallTimezoneAbbrev(call.schedule.timezone)}`;
};

export const formatOutboundCallCreatorLabel = (
  createdBy: Pick<OutboundCallFieldsFragment['createdBy'], 'name' | 'email'>
): string => {
  const name = createdBy.name?.trim();
  if (name) {
    return name;
  }
  const email = createdBy.email?.trim();
  if (email) {
    return email;
  }
  return 'Unknown';
};

export const formatOutboundCallStatus = (status: OutboundCallStatus): string => {
  switch (status) {
    case OutboundCallStatus.Scheduled:
      return 'Scheduled';
    case OutboundCallStatus.PendingConfirmation:
      return 'Pending confirmation';
    case OutboundCallStatus.Dialing:
      return 'Dialing';
    case OutboundCallStatus.InProgress:
      return 'In progress';
    case OutboundCallStatus.Completed:
      return 'Completed';
    case OutboundCallStatus.Failed:
      return 'Failed';
    case OutboundCallStatus.Cancelled:
      return 'Cancelled';
    case OutboundCallStatus.NoAnswer:
      return 'No answer';
    case OutboundCallStatus.VoicemailLeft:
      return 'Voicemail left';
    default:
      return status;
  }
};

export const isOutboundCallEditable = (status: OutboundCallStatus): boolean =>
  status === OutboundCallStatus.Scheduled || status === OutboundCallStatus.PendingConfirmation;

/** Scheduled / pending confirmation calls must be cancelled, not archived. */
export const isOutboundCallCancellable = isOutboundCallEditable;

/** Non-scheduled calls can be archived (removed from the active list). */
export const isOutboundCallArchivable = (status: OutboundCallStatus): boolean =>
  !isOutboundCallCancellable(status);

/** Split a selection into cancellable vs archivable outbound call IDs. */
export const partitionOutboundCallSelection = (
  calls: Array<Pick<OutboundCallFieldsFragment, 'id' | 'status'>>,
  selectedIds: Iterable<string>
): { cancellableIds: string[]; archivableIds: string[] } => {
  const selected = new Set(selectedIds);
  const cancellableIds: string[] = [];
  const archivableIds: string[] = [];
  for (const call of calls) {
    if (!selected.has(call.id)) {
      continue;
    }
    if (isOutboundCallCancellable(call.status)) {
      cancellableIds.push(call.id);
    } else if (isOutboundCallArchivable(call.status)) {
      archivableIds.push(call.id);
    }
  }
  return { cancellableIds, archivableIds };
};

/** Shared ActionResult error extraction for outbound list/form actions. */
export const getOutboundCallActionErrorMessage = (result: {
  applicationError?: { message?: string | null } | null;
  serverError?: { message?: string | null } | null;
  validationErrors?: Array<{ message: string }> | null;
}): string | null => {
  const applicationMessage = result.applicationError?.message || result.serverError?.message;
  if (applicationMessage) {
    return applicationMessage;
  }
  if (result.validationErrors?.length) {
    return result.validationErrors.map((error) => error.message).join(' ');
  }
  return null;
};

export const isOutboundCallDialable = (status: OutboundCallStatus): boolean =>
  status === OutboundCallStatus.Scheduled ||
  status === OutboundCallStatus.PendingConfirmation ||
  status === OutboundCallStatus.Failed;

export const isOutboundCallActive = (status: OutboundCallStatus): boolean =>
  status === OutboundCallStatus.Dialing || status === OutboundCallStatus.InProgress;

/** How often to refetch while a call is dialing / overdue-scheduled. */
export const OUTBOUND_CALLS_LIVE_POLL_MS = 4_000;

/**
 * Extra wait after scheduledAt before the first refetch, so the dial activity
 * has time to flip status off SCHEDULED.
 */
export const OUTBOUND_CALLS_SCHEDULE_REFRESH_BUFFER_MS = 1_500;

/** setTimeout clamps above this; keep waits within a safe range. */
const OUTBOUND_CALLS_MAX_WAIT_MS = 2_147_483_647;

export type OutboundCallsLiveRefreshPlan =
  | { mode: 'idle' }
  | { mode: 'poll'; intervalMs: number }
  | { mode: 'wait'; delayMs: number };

type OutboundCallLiveRefreshInput = {
  status: OutboundCallStatus;
  schedule: { scheduledAt?: Date | string | null };
};

/**
 * Decide how the outbound calls list should stay in sync with server status.
 * - poll: call is active, immediate, or past its scheduled time
 * - wait: next scheduled dial is in the future — refresh once it starts
 * - idle: nothing live to watch
 */
export const getOutboundCallsLiveRefreshPlan = (
  calls: OutboundCallLiveRefreshInput[],
  nowMs = Date.now()
): OutboundCallsLiveRefreshPlan => {
  let nextFutureMs: number | undefined;

  for (const call of calls) {
    if (isOutboundCallActive(call.status)) {
      return { mode: 'poll', intervalMs: OUTBOUND_CALLS_LIVE_POLL_MS };
    }
    if (call.status !== OutboundCallStatus.Scheduled) {
      continue;
    }
    const scheduledAt = call.schedule.scheduledAt;
    if (scheduledAt == null || scheduledAt === '') {
      return { mode: 'poll', intervalMs: OUTBOUND_CALLS_LIVE_POLL_MS };
    }
    const at = scheduledAt instanceof Date ? scheduledAt : new Date(scheduledAt);
    const atMs = at.getTime();
    if (Number.isNaN(atMs) || atMs <= nowMs) {
      return { mode: 'poll', intervalMs: OUTBOUND_CALLS_LIVE_POLL_MS };
    }
    if (nextFutureMs === undefined || atMs < nextFutureMs) {
      nextFutureMs = atMs;
    }
  }

  if (nextFutureMs === undefined) {
    return { mode: 'idle' };
  }

  const delayMs = Math.min(
    OUTBOUND_CALLS_MAX_WAIT_MS,
    Math.max(0, nextFutureMs - nowMs + OUTBOUND_CALLS_SCHEDULE_REFRESH_BUFFER_MS)
  );
  return { mode: 'wait', delayMs };
};

export const outboundCallStatusBadgeColor = (
  status: OutboundCallStatus
): 'yellow' | 'sky' | 'green' | 'red' | 'neutral' => {
  switch (status) {
    case OutboundCallStatus.Scheduled:
    case OutboundCallStatus.PendingConfirmation:
      return 'yellow';
    case OutboundCallStatus.Dialing:
    case OutboundCallStatus.InProgress:
      return 'sky';
    case OutboundCallStatus.Completed:
    case OutboundCallStatus.VoicemailLeft:
      return 'green';
    case OutboundCallStatus.Failed:
    case OutboundCallStatus.NoAnswer:
      return 'red';
    case OutboundCallStatus.Cancelled:
      return 'neutral';
    default:
      return 'neutral';
  }
};

export const truncateOutboundText = (value: string, maxLength: number): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '—';
  }
  if (trimmed.length <= maxLength) {
    return trimmed;
  }
  return `${trimmed.slice(0, maxLength)}…`;
};

export const isOutboundCallDetailLive = (status: OutboundCallStatus): boolean =>
  isOutboundCallActive(status) || status === OutboundCallStatus.Scheduled;

export const resolveVoiceAgentCallbackPhonePlaceholder = (
  agent?: {
    agency?: { phone?: string | null } | null;
    configuration?: { agency?: { phone?: string | null } | null } | null;
  } | null
): string => agent?.configuration?.agency?.phone || agent?.agency?.phone || '+1 (555) 123-4567';
